import { BlockerItem } from '../pages/YourTeams/components/SprintSummary/types';
import { TeamIdentity } from '../types';

import { fetchBlockersForSprint } from './blockers';
import { fetchTeamSprints } from './sprints';

export interface TeamBlockers {
  team: TeamIdentity;
  blockers: BlockerItem[];
}

/**
 * Fetches the active sprint for a team
 * @param teamId The team ID to fetch the active sprint for
 * @returns The active sprint ID or null if no active sprint is found
 */
export async function fetchActiveSprintForTeam(
  teamId: string,
): Promise<string | null> {
  try {
    const sprints = await fetchTeamSprints(teamId);
    // Find the active sprint (status = 'active')
    const activeSprint = sprints.find((sprint) => sprint.status === 'active');
    return activeSprint?.id || null;
  } catch (error) {
    // Log error but don't throw to prevent dashboard from breaking
    // eslint-disable-next-line no-console
    console.error(`Error fetching active sprint for team ${teamId}:`, error);
    return null;
  }
}

/**
 * Fetches blockers for all teams
 * @param teams The list of teams to fetch blockers for
 * @returns A list of teams with their blockers
 */
export async function fetchAllTeamsBlockers(
  teams: TeamIdentity[],
): Promise<TeamBlockers[]> {
  const teamBlockersPromises = teams.map(async (team) => {
    try {
      // Get the active sprint for this team
      const activeSprintId = await fetchActiveSprintForTeam(team.id);

      // If there's no active sprint, return empty blockers
      if (!activeSprintId) {
        return { team, blockers: [] };
      }

      // Fetch blockers for the active sprint
      const blockers = await fetchBlockersForSprint(activeSprintId);

      return { team, blockers };
    } catch (error) {
      // Log error but don't throw to prevent dashboard from breaking
      // eslint-disable-next-line no-console
      console.error(`Error fetching blockers for team ${team.id}:`, error);
      return { team, blockers: [] };
    }
  });

  // Wait for all promises to resolve
  const teamBlockers = await Promise.all(teamBlockersPromises);

  // Filter out teams with no blockers
  return teamBlockers.filter((tb) => tb.blockers.length > 0);
}
